import {actionChannel, call, cancel, join, put, select, spawn, take} from 'redux-saga/effects';
import {v4 as uuidv4} from 'uuid';
import {matchPath} from '@computerrock/formation-router';
import fetchRequest from '../../application/sagas/fetchRequest';
import * as serviceCaseActionTypes from '../serviceCaseActionTypes';
import config from '../../config';
import routePaths from '../../routePaths';

const pendingLocks = new Map();
const pendingUnlocks = new Map();
const lockVersions = new Map();

const sendLockRequest = function* sendLockRequest(ecsFlowService, request, lockUUID, caller) {
    const {serviceCaseId, requestId} = request;
    let responses;
    try {
        const unlock = pendingUnlocks.get(serviceCaseId);
        if (unlock) yield join(unlock.task);

        responses = yield actionChannel(action => [
            serviceCaseActionTypes.LOCK_SERVICE_CASE_REQUEST_SUCCEEDED,
            serviceCaseActionTypes.LOCK_SERVICE_CASE_REQUEST_FAILED,
        ].includes(action.type) && action.meta?.requestId === requestId);

        yield call(fetchRequest, serviceCaseActionTypes.LOCK_SERVICE_CASE_REQUEST, ecsFlowService.lockServiceCase, {
            serviceCaseId,
            lockData: {lockUUID: lockUUID || uuidv4()},
        }, {caller, requestId});
        return yield take(responses);
    } finally {
        responses?.close();
        if (pendingLocks.get(serviceCaseId) === request) pendingLocks.delete(serviceCaseId);
    }
};

/**
 * All callers share an in-flight request for a case, including route loaders and saves.
 */
export const startServiceCaseLockRequest = function* startServiceCaseLockRequest(
    ecsFlowService, serviceCaseId, {caller, lockUUID, isNavigation = false} = {},
) {
    const pending = pendingLocks.get(serviceCaseId);
    if (pending) {
        const unlock = pendingUnlocks.get(serviceCaseId);
        if (isNavigation && unlock && !unlock.isSent) yield cancel(unlock.task);
        return pending;
    }

    const request = {serviceCaseId, requestId: uuidv4()};
    pendingLocks.set(serviceCaseId, request);
    request.task = yield spawn(sendLockRequest, ecsFlowService, request, lockUUID, caller);
    return request;
};

/**
 * A combined save passes a fresh lockContext to its steps.
 * Its result is reused only within that operation, including failures; later operations may retry.
 */
export const requestServiceCaseLock = function* requestServiceCaseLock(ecsFlowService, serviceCaseId, {
    caller, lockUUID, lockContext,
} = {}) {
    const version = lockVersions.get(serviceCaseId) || 0;
    if (lockContext?.serviceCaseId === serviceCaseId
        && lockContext.ecsFlowService === ecsFlowService
        && lockContext.version === version
        && Date.now() < lockContext.expiresAt) return lockContext.response;

    const request = yield* startServiceCaseLockRequest(ecsFlowService, serviceCaseId, {caller, lockUUID});
    const response = yield join(request.task);
    if (lockContext) {
        Object.assign(lockContext, {
            serviceCaseId,
            ecsFlowService,
            version,
            response,
            timerReset: false,
            expiresAt: Date.now() + config.DEFAULT_SERVICE_CASE_LOCK_TIME * 1000,
        });
    }
    return response;
};

export const resetServiceCaseLockTime = function* resetServiceCaseLockTime(serviceCaseId, lockContext) {
    if (lockContext?.timerReset) return;
    if (lockContext && lockContext.version !== (lockVersions.get(serviceCaseId) || 0)) return;
    const {location} = yield select(state => state.router);
    const match = matchPath(location.pathname, {path: routePaths.SERVICE_CASE_SECTION});
    if (match?.params.serviceCaseId !== serviceCaseId) return;

    yield put({
        type: serviceCaseActionTypes.SET_SERVICE_CASE_REMAINING_LOCK_TIME,
        payload: {remainingLockTime: config.DEFAULT_SERVICE_CASE_LOCK_TIME},
    });
    if (lockContext) lockContext.timerReset = true;
};

const sendUnlockRequest = function* sendUnlockRequest(ecsFlowService, unlock) {
    const {serviceCaseId} = unlock;
    try {
        const pending = pendingLocks.get(serviceCaseId);
        if (pending) yield join(pending.task);

        unlock.isSent = true;
        yield call(fetchRequest, serviceCaseActionTypes.UNLOCK_SERVICE_CASE_REQUEST, function* unlockRequest(params) {
            const response = yield call(ecsFlowService.unlockServiceCase, params);
            // The installed EF client returns raw fetch Responses, including HTTP errors.
            if (response?.ok === false) {
                const error = new Error(`Unlocking service case ${serviceCaseId} failed (HTTP ${response.status})`);
                error.status = response.status;
                throw error;
            }
            return response;
        }, {serviceCaseId});
    } finally {
        if (pendingUnlocks.get(serviceCaseId) === unlock) pendingUnlocks.delete(serviceCaseId);
    }
};

export const startServiceCaseUnlockRequest = function* startServiceCaseUnlockRequest(ecsFlowService, serviceCaseId) {
    lockVersions.set(serviceCaseId, (lockVersions.get(serviceCaseId) || 0) + 1);
    const unlock = {serviceCaseId, isSent: false};
    pendingUnlocks.set(serviceCaseId, unlock);
    unlock.task = yield spawn(sendUnlockRequest, ecsFlowService, unlock);
};
